<script>
import {useCatalogStore} from "@js/store/CatalogStore.js";
import {checkDocument, getLocalStorage} from "@js/helpers.ts";
import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue";
import CatalogMobileFilter from "@js/components/Sections/Catalog/CatalogMobileFilter.vue";
import Breadcrumbs from "@js/components/Breadcrumbs/Breadcrumbs.vue";
import NumberFormatComponent from "@js/components/Formatter/NumberFormatComponent.vue";
import OnlyDesktop from "@js/components/DynamicComponents/OnlyDesktop.vue";
import CatalogFilter from "@js/components/Sections/Catalog/CatalogFilter.vue";
import IconTrash from "@js/components/Icons/IconTrash.vue";
import CatalogSortBlock from "@js/components/Sections/Catalog/Components/CatalogSortBlock.vue";
import CatalogGridSwitcher from "@js/components/Sections/Catalog/Components/CatalogGridSwitcher.vue";
import ModalSelect from "@js/components/Input/ModalSelect.vue";
import IconChevronDown from "@js/components/Icons/IconChevronDown.vue";
import IconSimpleFilter from "@js/components/Icons/IconSimpleFilter.vue";
import CatalogProductItemWide from "@js/components/Sections/Catalog/Components/CatalogProductItemWide.vue";
import CatalogProductItemGrid from "@js/components/Sections/Catalog/Components/CatalogProductItemGrid.vue";
import CatalogProductSkeleton from "@js/components/Sections/Catalog/Components/CatalogProductSkeleton.vue";
import CantFindProduct from "@js/components/Sections/Catalog/Components/CantFindProduct.vue";
import Pagination from "@js/components/Pagination/Pagination.vue";
import FastBuyCallback from "@js/components/Callbacks/FastBuyCallback.vue";
import CatalogSelectedFilterTags from "@js/components/Sections/Catalog/Components/CatalogSelectedFilterTags.vue";
import CatalogCategoryNav from "@js/components/Sections/Catalog/Components/CatalogCategoryNav.vue";

export default {
    name: "CatalogComponent",
    props: {
        catalog_title: {
            required: false,
            type: String,
        }
    },
    components: {
        CatalogCategoryNav,
        CatalogSelectedFilterTags,
        FastBuyCallback,
        Pagination,
        CantFindProduct,
        CatalogProductSkeleton,
        CatalogProductItemGrid,
        CatalogProductItemWide,
        IconSimpleFilter,
        IconChevronDown,
        ModalSelect,
        CatalogGridSwitcher,
        CatalogSortBlock,
        IconTrash,
        CatalogFilter,
        OnlyDesktop,
        NumberFormatComponent,
        Breadcrumbs,
        CatalogMobileFilter,
        OnlyMobile
    },

    data() {
        return {
            _ecomSentImpressions: new Set(),
            _ecomContextKey: null,
            _ecomPushTimer: null,
        };
    },

    computed: {
        ecomListName() {
            return this.catalog_title || "Каталог";
        },
        ecomPage() {
            return Number(this.catalogStore?.pagination?.current_page || 1);
        },
        ecomPerPage() {
            return Number(this.catalogStore?.filter?.per_page || 30);
        },
        ecomContextFingerprint() {
            try {
                const url = new URL(window.location.href);
                const sort = this.catalogStore?.filter?.sort ?? "";
                return [
                    url.pathname,
                    url.search,
                    `page=${this.ecomPage}`,
                    `per_page=${this.ecomPerPage}`,
                    `sort=${typeof sort === 'string' ? sort : JSON.stringify(sort)}`
                ].join("|");
            } catch {
                return `page=${this.ecomPage}|per_page=${this.ecomPerPage}`;
            }
        },
    },

    watch: {
        'catalogStore.products': {
            handler() {
                clearTimeout(this._ecomPushTimer);
                this._ecomPushTimer = setTimeout(() => {
                    this._ecomTryPushImpressions();
                }, 150);
            },
            deep: true,
        },
        'catalogStore.pagination.current_page'() {
            clearTimeout(this._ecomPushTimer);
            this._ecomPushTimer = setTimeout(() => {
                this._ecomTryPushImpressions();
            }, 150);
        },
        'catalogStore.filter.per_page'() {
            clearTimeout(this._ecomPushTimer);
            this._ecomPushTimer = setTimeout(() => {
                this._ecomTryPushImpressions();
            }, 150);
        },
        'catalogStore.filter.sort': {
            handler() {
                clearTimeout(this._ecomPushTimer);
                this._ecomPushTimer = setTimeout(() => {
                    this._ecomTryPushImpressions();
                }, 150);
            },
            deep: true,
        },
    },

    methods: {
        _ecomDlPush(payload) {
            try {
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push(payload);
            } catch (e) {
                console.error("[ecom] push error:", e, payload);
            }
        },

        _ecomMapProduct(product, indexOnPage) {
            const position = (this.ecomPage - 1) * this.ecomPerPage + (indexOnPage + 1);

            return {
                id: String(product?.id ?? ""),
                name: product?.title ?? "Товар",
                price: Number(product?.display_price ?? product?.price_range?.min ?? 0) || 0,
                brand: "ФУЛЛ СТРОЙ",
                category: product?.category?.title || undefined,
                variant: product?.display_unit || 'шт',
                list: this.ecomListName,
                position,
            };
        },

        _ecomProductKey(mappedProduct) {
            return `${this._ecomContextKey ?? 'ctx'}::${mappedProduct.id}::${mappedProduct.position}`;
        },

        _ecomTryPushImpressions() {
            if (!this.catalogStore?.loadings?.catalog_loaded) return;

            const products = this.catalogStore?.products || [];
            if (!Array.isArray(products) || products.length === 0) return;

            const nextContextKey = this.ecomContextFingerprint;
            if (this._ecomContextKey !== nextContextKey) {
                this._ecomContextKey = nextContextKey;
                this._ecomSentImpressions.clear();
            }

            const toSend = [];
            products.forEach((p, i) => {
                const mapped = this._ecomMapProduct(p, i);
                const key = this._ecomProductKey(mapped);
                if (!this._ecomSentImpressions.has(key)) {
                    toSend.push({mapped, key});
                }
            });

            if (!toSend.length) return;

            const chunkSize = 20;
            for (let i = 0; i < toSend.length; i += chunkSize) {
                const slice = toSend.slice(i, i + chunkSize);
                const impressions = slice.map(({mapped}) => mapped);

                this._ecomDlPush({
                    ecommerce: {
                        currencyCode: "RUB",
                        impressions,
                    },
                });

                slice.forEach(({key}) => this._ecomSentImpressions.add(key));
            }
        },
    },

    mounted() {
        if (checkDocument()) {
            this.catalogStore.grid_mode = getLocalStorage('grid_mode')
                ? getLocalStorage('grid_mode')
                : this.catalogStore.grid_mods.wide;
        }

        this._ecomTryPushImpressions();
    },

    beforeMount() {
        this.catalogStore.initFromUrl();
    },

    beforeUnmount() {
        clearTimeout(this._ecomPushTimer);
    },

    setup() {
        const catalogStore = useCatalogStore();
        catalogStore.is_filter_watcher_on = false;
        return {catalogStore};
    }
}
</script>

<template>
    <OnlyMobile :maxSize="1024">
        <CatalogMobileFilter/>
    </OnlyMobile>

    <div class="container">
        <OnlyMobile :maxSize="1024">
            <Breadcrumbs/>
            <div class="flex md:items-center justify-between mb-3 gap-5">
                <h1 v-if="catalog_title" class="font-medium">
                    {{ catalog_title }}
                </h1>
                <div class="flex md:items-center gap-1 max-md:mt-1.5" v-if="catalogStore.loadings.catalog_loaded">
                    <div class="flex gap-1 bg-gray-200 rounded px-2 py-1 text-nowrap h-min max-md:text-xs font-semibold">
                        Найдено:
                        <div v-if="catalogStore.total_result && catalogStore.total_result > 0">
                            <NumberFormatComponent :number="catalogStore.total_result"/>
                        </div>
                    </div>
                </div>
                <div v-else>
                    <div class="flex md:items-center gap-1 max-md:mt-1.5">
                        <div role="status" class="max-w-sm animate-pulse">
                            <div class="h-6 bg-gray-300 rounded w-20 animate-pulse"></div>
                        </div>
                    </div>
                </div>
            </div>
        </OnlyMobile>

        <div class="grid grid-cols-12 gap-y-10 md:gap-x-10 text-black">
            <div class="hidden md:block col-span-12 md:col-span-4 lg:col-span-3">
                <OnlyDesktop>
                    <CatalogFilter class="mb-10"/>
                </OnlyDesktop>
            </div>

            <div class="col-span-12 md:col-span-8 lg:col-span-9 flex flex-col gap-3 md:gap-5">
                <div class="col-span-12">
                    <OnlyDesktop :maxSize="1024">
                        <div class="col-span-12 font-medium">
                            <Breadcrumbs class="-mb-2"/>
                            <h1 v-if="catalog_title" class="font-medium text-2xl -mb-2">
                                {{ catalog_title }}
                            </h1>
                        </div>
                    </OnlyDesktop>

                    <CatalogCategoryNav/>

                    <OnlyMobile :maxSize="1024" v-if="catalogStore.selected_filters.length > 0">
                        <div class="flex items-start mb-2 mt-5">
                            <div>
                                <div
                                    @click.prevent="catalogStore.resetFilter(); catalogStore.initFromUrl()"
                                    class="flex items-center gap-2 text-xs text-nowrap font-semibold cursor-pointer py-2 px-4 rounded md:rounded-full bg-gray-300 md:hover:bg-baseColor md:hover:text-black"
                                >
                                    <IconTrash class="w-4 h-4 text-gray-700 group-hover:text-black transition"/>
                                    Очистить все
                                </div>
                            </div>
                            <div class="ms-2">
                                <div class="vertical-divider"></div>
                            </div>
                            <div class="flex items-start overflow-x-auto">
                                <CatalogSelectedFilterTags/>
                            </div>
                        </div>
                    </OnlyMobile>
                </div>

                <div class="col-span-12 flex items-center justify-between">
                    <CatalogSortBlock/>

                    <div class="flex items-center gap-2">
                        <OnlyDesktop class="flex gap-2 w-min">
                            <CatalogGridSwitcher/>
                            <div class="flex items-center gap-2">
                                <ModalSelect buttonClass="border border-gray-300 bg-transparent text-gray-600 rounded text-sm px-3 py-1">
                                    <template #button>
                                        {{ catalogStore.filter.per_page }}
                                        <IconChevronDown class="w-4 h-4 text-gray-600 ms-1"/>
                                    </template>

                                    <template #menu>
                                        <ul class="py-2 text-sm text-gray-700">
                                            <li
                                                v-for="per_page in catalogStore.per_page_array"
                                                :key="per_page"
                                                @click.prevent="catalogStore.changePerPage(per_page)"
                                                class="block px-4 py-2 font-medium cursor-pointer md:hover:bg-grayBg"
                                            >
                                                <div class="text-xs font-bold text-nowrap">
                                                    {{ per_page }} товаров
                                                </div>
                                            </li>
                                        </ul>
                                    </template>
                                </ModalSelect>
                            </div>
                        </OnlyDesktop>

                        <OnlyMobile
                            :maxSize="1024"
                            class="inline-flex items-center text-sm font-medium bg-baseColor text-black px-7 py-1.5 rounded leading-tight"
                            @click.prevent="catalogStore.toggleFilter"
                        >
                            <IconSimpleFilter class="min-h-4 max-h-4 min-w-4 max-w-4 me-2"/>
                            Фильтр
                            <span v-if="catalogStore.selected_filters.length" class="ms-1">
                                ({{ catalogStore.selected_filters.length }})
                            </span>
                        </OnlyMobile>
                    </div>
                </div>

                <template v-if="catalogStore.selected_filters.length">
                    <OnlyDesktop :maxSize="1024" class="col-span-12">
                        <div class="w-full p-0 md:px-5 md:py-2 md:bg-white md:border md:border-gray-200 md:shadow rounded-xl">
                            <div class="flex items-center flex-wrap">
                                <div
                                    @click.prevent="catalogStore.resetFilter(); catalogStore.initFromUrl()"
                                    class="group flex items-center justify-center transition cursor-pointer w-[40px] h-[40px] p-0.5 border border-gray-800 md:hover:bg-baseColor md:hover:border-baseColor rounded-full"
                                >
                                    <IconTrash class="w-5 h-5 text-gray-700 group-hover:text-gray-700 transition"/>
                                </div>
                                <div class="ms-3 me-1">
                                    <div class="vertical-divider"></div>
                                </div>
                                <CatalogSelectedFilterTags/>
                            </div>
                        </div>
                    </OnlyDesktop>
                </template>

                <div
                    class="grid gap-2 md:gap-3"
                    :class="[catalogStore.grid_mode === catalogStore.grid_mods.wide ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3']"
                >
                    <div
                        v-if="catalogStore.loadings.catalog_loaded"
                        v-for="(product, index) in catalogStore.products"
                        :key="product.id"
                    >
                        <CatalogProductItemWide
                            v-if="catalogStore.grid_mode === catalogStore.grid_mods.wide"
                            :data="product"
                            :position="(ecomPage - 1) * ecomPerPage + index + 1"
                        />
                        <CatalogProductItemGrid
                            v-else
                            :data="product"
                            :position="(ecomPage - 1) * ecomPerPage + index + 1"
                        />
                    </div>

                    <template v-else>
                        <CatalogProductSkeleton v-for="i in 30" :key="'skeleton-'+i"/>
                    </template>
                </div>

                <div
                    v-if="catalogStore.loadings.catalog_loaded"
                    class="row d-flex align-items-center justify-content-center"
                >
                    <CantFindProduct class="mb-5"/>
                    <Pagination
                        v-if="catalogStore.pagination.total_pages"
                        :current-page="catalogStore.pagination.current_page"
                        :total-pages="catalogStore.pagination.total_pages"
                        @update:current-page="catalogStore.onPageChange"
                    />
                </div>
            </div>
        </div>
    </div>

    <FastBuyCallback/>
</template>

<style scoped lang="scss">
.vertical-divider {
    width: 2px;
    border-radius: 100px;
    height: 2rem;
    background-color: var(--main-color);
}
</style>
